import axios from 'axios';
import { DateTime } from 'luxon';
import { useContext, useEffect, useMemo, useState } from 'react';
import { config } from '../../../modules/config';
import type { SuccessEnvelop } from '../../../modules/context/guestAuthContext';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import type { ReservationState } from '../../../modules/enums/types';
import { useAuth, useNotifications } from '../../../modules/hooks';
import type { Reservations, ReservationsAPIResponse } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';

export interface ReservationOption {
  /** Internal reservation uuid (the id the access-code endpoints expect). */
  reservationId: string;
  guest: string;
  room: string;
  /** Display-formatted dates. */
  arrival: string;
  departure: string;
  /** Raw ISO window, for callers that need to send it (e.g. create). */
  startDate?: string;
  endDate?: string;
}

interface UseReservationOptionsArgs {
  /** Only fetch while true (e.g. the modal/section is shown). */
  active: boolean;
  /** Reservation states to request (pass a stable array). */
  states: ReservationState[];
  /** Internal reservation uuids to hide (pass a memoized Set). */
  excludeReservationIds?: Set<string>;
}

// Safety cap so a misbehaving cursor can't loop forever.
const MAX_PAGES = 50;

const formatDate = (value?: string | null): string =>
  value ? DateTime.fromISO(value).toFormat('dd LLL yyyy') : '';

/**
 * Loads all property reservations for the given states (paging the cursor) and maps them to
 * picker options, excluding any whose internal uuid is in `excludeReservationIds`. Single
 * source of truth for the reservation fetch/filter used by both the Promote and Create flows.
 */
export const useReservationOptions = ({
  active,
  states,
  excludeReservationIds,
}: UseReservationOptionsArgs): { options: ReservationOption[]; loading: boolean } => {
  const { setSimpleToasts } = useNotifications()!;
  const { credentialsInfo } = useAuth()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const token = credentialsInfo?.token;
  const propertyId = selectedProperty?.property.uuid;
  const statusParam = states.join(',');

  const [reservations, setReservations] = useState<Reservations[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active) return;
    setReservations([]);
    if (!token) return;

    let cancelled = false;
    const loadAll = async () => {
      setLoading(true);
      try {
        const collected: Reservations[] = [];
        let cursor: string | undefined;
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const { data } = await axios.get<SuccessEnvelop<ReservationsAPIResponse>>(
            endpoints.MEWS_RESERVATIONS,
            {
              baseURL: config.bookingEngine.endpoint,
              headers: {
                authorization: token,
                ...(propertyId ? { 'property-id': propertyId } : {}),
              },
              params: {
                status: statusParam,
                ...(cursor ? { Cursor: cursor } : {}),
              },
            },
          );
          const batch = data.result.Reservations ?? [];
          collected.push(...batch);
          cursor = data.result.Cursor;
          if (!cursor || batch.length === 0) break;
        }
        if (!cancelled) setReservations(collected);
      } catch {
        if (!cancelled) {
          setSimpleToasts({
            type: 'danger',
            message: 'Failed to load reservations',
            show: true,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();

    return () => {
      cancelled = true;
    };
  }, [active, statusParam, token, propertyId, setSimpleToasts]);

  const options = useMemo<ReservationOption[]>(() => {
    return reservations.flatMap((reservation) => {
      const internal = reservation.InternalReservation;
      if (!internal?.uuid) return [];
      if (excludeReservationIds?.has(internal.uuid)) return [];

      const guest =
        [internal.guestFirstName, internal.guestLastName].filter(Boolean).join(' ') ||
        [reservation.Customer?.FirstName, reservation.Customer?.LastName]
          .filter(Boolean)
          .join(' ') ||
        'Guest';
      const room = internal.roomName || reservation.Resource?.ResourceName || '—';
      const start = reservation.ScheduledStartUtc || reservation.StartUtc;

      return [
        {
          reservationId: internal.uuid,
          guest,
          room,
          arrival: formatDate(start),
          departure: formatDate(reservation.EndUtc),
          startDate: start,
          endDate: reservation.EndUtc,
        },
      ];
    });
  }, [reservations, excludeReservationIds]);

  return { options, loading };
};
